/* ==================================================================
   ON.PRO — Motion engine (Phase 2)
   Vanilla JS, no dependencies. IntersectionObserver for trigger-once
   effects; one shared rAF loop for scroll-linked effects (tilt,
   parallax). Fully gated by prefers-reduced-motion.

   Hooks (data attributes):
     [data-onpro-reveal]            fade/slide in once on scroll
       data-onpro-delay="120"       optional stagger delay (ms)
     [data-onpro-words]             reveal heading word-by-word
     [data-onpro-tilt="14"]         3D rotate as it passes (max deg)
     [data-onpro-parallax="0.15"]   move slower than scroll (factor)
     [data-onpro-count="24"]        count up from 0 when in view
       data-onpro-decimals data-onpro-suffix data-onpro-prefix
       data-onpro-duration (ms)
     [data-onpro-invert]            mobile: invert when centered
   ================================================================== */
(function () {
  'use strict';

  var prefersReduced = false;
  try {
    prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  var canHover = false;
  try {
    canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch (e) {}

  var hasIO = 'IntersectionObserver' in window;

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  function $all(sel) {
    return Array.prototype.slice.call(document.querySelectorAll(sel));
  }

  ready(function () {

    /* ---- 1. Reveal on scroll ---- */
    var reveals = $all('[data-onpro-reveal]');
    if (reveals.length) {
      if (prefersReduced || !hasIO) {
        reveals.forEach(function (el) { el.classList.add('is-visible'); });
      } else {
        var revealIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var delay = parseFloat(entry.target.getAttribute('data-onpro-delay')) || 0;
            if (delay) entry.target.style.transitionDelay = delay + 'ms';
            entry.target.classList.add('is-visible');
            revealIO.unobserve(entry.target);
          });
        }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 });
        reveals.forEach(function (el) { revealIO.observe(el); });
      }
    }

    /* ---- 2. Word-by-word headline ---- */
    $all('[data-onpro-words]').forEach(function (head) {
      if (head.dataset.onproWordsInit) return;
      head.dataset.onproWordsInit = '1';
      wrapWords(head);
      var words = $all('.onpro-word').filter(function (w) { return head.contains(w); });
      if (prefersReduced || !hasIO) {
        words.forEach(function (w) { w.classList.add('is-in'); });
        return;
      }
      var wordIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          words.forEach(function (w, i) {
            setTimeout(function () { w.classList.add('is-in'); }, i * 70);
          });
          wordIO.unobserve(entry.target);
        });
      }, { threshold: 0.3 });
      wordIO.observe(head);
    });

    function wrapWords(el) {
      var nodes = Array.prototype.slice.call(el.childNodes);
      el.innerHTML = '';
      nodes.forEach(function (node) {
        if (node.nodeType === 3) {
          var parts = node.textContent.split(/(\s+)/);
          parts.forEach(function (part) {
            if (part === '') return;
            if (/^\s+$/.test(part)) {
              el.appendChild(document.createTextNode(part));
            } else {
              var span = document.createElement('span');
              span.className = 'onpro-word';
              span.textContent = part;
              el.appendChild(span);
            }
          });
        } else {
          el.appendChild(node);
        }
      });
    }

    /* ---- 3 + 4. Scroll-linked 3D tilt and parallax ---- */
    var tilts = $all('[data-onpro-tilt]');
    var paras = $all('[data-onpro-parallax]');
    if (!prefersReduced && (tilts.length || paras.length)) {
      var ticking = false;

      function update() {
        ticking = false;
        var vh = window.innerHeight || document.documentElement.clientHeight;

        tilts.forEach(function (el) {
          var r = el.getBoundingClientRect();
          var center = r.top + r.height / 2;
          var p = (center - vh / 2) / (vh / 2 + r.height / 2);
          p = Math.max(-1, Math.min(1, p));
          var max = parseFloat(el.getAttribute('data-onpro-tilt')) || 14;
          var ry = (-p * max).toFixed(2);
          var rx = (p * max * 0.32).toFixed(2);
          el.style.transform =
            'perspective(1100px) rotateY(' + ry + 'deg) rotateX(' + rx + 'deg)';
        });

        paras.forEach(function (el) {
          var r = el.getBoundingClientRect();
          var center = r.top + r.height / 2;
          var p = (center - vh / 2) / vh;
          var speed = parseFloat(el.getAttribute('data-onpro-parallax')) || 0.15;
          el.style.transform =
            'translate3d(0,' + (-p * speed * 100).toFixed(2) + 'px,0)';
        });
      }

      function onScroll() {
        if (!ticking) { ticking = true; requestAnimationFrame(update); }
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    }

    /* ---- 5. Count-up spec numbers ---- */
    var counters = $all('[data-onpro-count]');
    if (counters.length) {
      if (prefersReduced || !hasIO) {
        counters.forEach(function (el) { setCount(el, targetVal(el)); });
      } else {
        var countIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            runCount(entry.target);
            countIO.unobserve(entry.target);
          });
        }, { threshold: 0.6 });
        counters.forEach(function (el) { countIO.observe(el); });
      }
    }

    function targetVal(el) { return parseFloat(el.getAttribute('data-onpro-count')) || 0; }
    function decimalsOf(el) { return parseInt(el.getAttribute('data-onpro-decimals'), 10) || 0; }

    function setCount(el, val) {
      var prefix = el.getAttribute('data-onpro-prefix') || '';
      var suffix = el.getAttribute('data-onpro-suffix') || '';
      el.textContent = prefix + val.toFixed(decimalsOf(el)) + suffix;
    }

    function runCount(el) {
      var target = targetVal(el);
      var dec = decimalsOf(el);
      var dur = parseInt(el.getAttribute('data-onpro-duration'), 10) || 1400;
      var prefix = el.getAttribute('data-onpro-prefix') || '';
      var suffix = el.getAttribute('data-onpro-suffix') || '';
      var start = null;
      function frame(now) {
        if (start === null) start = now;
        var t = Math.min(1, (now - start) / dur);
        var eased = 1 - Math.pow(1 - t, 3);
        el.textContent = prefix + (target * eased).toFixed(dec) + suffix;
        if (t < 1) requestAnimationFrame(frame);
        else el.textContent = prefix + target.toFixed(dec) + suffix;
      }
      requestAnimationFrame(frame);
    }

    /* ---- 6. Mobile center-screen inversion (no hover) ---- */
    var inverts = $all('[data-onpro-invert]');
    if (inverts.length && !canHover && !prefersReduced && hasIO) {
      var invertIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          entry.target.classList.toggle('is-inverted', entry.isIntersecting);
        });
      }, { rootMargin: '-42% 0px -42% 0px', threshold: 0 });
      inverts.forEach(function (el) { invertIO.observe(el); });
    }

    /* ---- 7. Flavor card flip ----
       Desktop (hover-capable): pure CSS — :hover / :focus-within flips.
       Touch (no hover): tap anywhere on the card toggles the flip; taps
       inside [data-onpro-no-flip] (the Add-to-cart) are ignored so the
       button works without flipping the card back. inert keeps the
       hidden back face out of the tab order / a11y tree until shown. */
    if (!canHover) {
      $all('[data-onpro-flip]').forEach(function (flip) {
        var back = flip.querySelector('.onpro-flip__back');
        if (back) back.inert = true;
        flip.addEventListener('click', function (e) {
          if (e.target.closest('[data-onpro-no-flip]')) return;
          var flipped = flip.classList.toggle('is-flipped');
          flip.setAttribute('aria-expanded', flipped ? 'true' : 'false');
          if (back) back.inert = !flipped;
        });
      });
    }

    /* ---- 8. Reviews carousel: active-dot tracking + click-to-scroll ---- */
    /* Scrolling/snapping is pure CSS (scroll-snap). This only keeps the
       dot indicator in sync and lets dots jump to a slide. */
    $all('[data-onpro-carousel]').forEach(function (track) {
      var items = $all('[data-onpro-carousel-item]').filter(function (el) {
        return track.contains(el);
      });
      var dotsWrap = track.parentNode
        ? track.parentNode.querySelector('[data-onpro-carousel-dots]')
        : null;
      if (!dotsWrap) return;
      var dots = $all('.onpro-reviews__dot').filter(function (d) {
        return dotsWrap.contains(d);
      });

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          if (items[i]) {
            items[i].scrollIntoView({
              behavior: prefersReduced ? 'auto' : 'smooth',
              inline: 'start',
              block: 'nearest'
            });
          }
        });
      });

      if (hasIO) {
        var carIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var idx = items.indexOf(entry.target);
            dots.forEach(function (d, di) {
              var on = di === idx;
              d.classList.toggle('is-active', on);
              if (on) { d.setAttribute('aria-current', 'true'); }
              else { d.removeAttribute('aria-current'); }
            });
          });
        }, { root: track, threshold: 0.6 });
        items.forEach(function (it) { carIO.observe(it); });
      }
    });

    /* ---- 9. Sticky header: shadow/border once scrolled off the top ---- */
    var docEl = document.documentElement;
    var navTicking = false;
    function navScroll() {
      if (navTicking) return;
      navTicking = true;
      requestAnimationFrame(function () {
        docEl.classList.toggle('onpro-nav-scrolled', window.scrollY > 8);
        navTicking = false;
      });
    }
    window.addEventListener('scroll', navScroll, { passive: true });
    navScroll();

    /* ---- 10. Product page: variant-aware star rating ---- */
    /* Dawn updates the product info via targeted DOM swaps, so the
       rating near the title is refreshed here when the flavor variant
       changes (delegated change listener within <product-info>). */
    $all('[data-onpro-rating]').forEach(function (el) {
      var map;
      try { map = JSON.parse(el.getAttribute('data-onpro-rating')); }
      catch (e) { return; }
      var label = el.getAttribute('data-onpro-reviews-label') || 'reviews';
      var scope = el.closest('product-info') || document;
      var fill = el.querySelector('.onpro-rating__stars-fill');
      var meta = el.querySelector('.onpro-rating__meta');

      function currentFlavor() {
        var inputs = scope.querySelectorAll('input:checked, select');
        for (var i = 0; i < inputs.length; i++) {
          var v = (inputs[i].value || '').toLowerCase();
          if (v.indexOf('chocolate') > -1) return 'chocolate';
          if (v.indexOf('vanilla') > -1) return 'vanilla';
          if (v.indexOf('strawberr') > -1) return 'strawberry';
        }
        return null;
      }

      function update() {
        var flv = currentFlavor();
        if (!flv || !map[flv]) return;
        var d = map[flv];
        if (fill) fill.style.width = (d.r / 5 * 100) + '%';
        if (meta) meta.textContent = d.r.toFixed(1) + ' (' + d.c + ' ' + label + ')';
        el.setAttribute('aria-label', d.r.toFixed(1) + ' / 5, ' + d.c + ' ' + label);
      }

      scope.addEventListener('change', function (e) {
        if (e.target && (e.target.matches('input') || e.target.matches('select'))) update();
      });
      update();
    });

    /* ---- 11. Email capture popup ---- */
    var popup = document.querySelector('[data-onpro-popup]');
    if (popup) {
      var POPUP_KEY = 'onpro_popup_dismissed';
      var freqDays = parseInt(popup.getAttribute('data-frequency'), 10) || 7;
      var delayMs = (parseFloat(popup.getAttribute('data-delay')) || 4) * 1000;
      var hasSuccess = !!popup.querySelector('[data-onpro-popup-success]');

      function popupDismissed() {
        try {
          var t = parseInt(localStorage.getItem(POPUP_KEY), 10);
          return t && (Date.now() - t) < freqDays * 86400000;
        } catch (e) { return false; }
      }
      function popupRemember() {
        try { localStorage.setItem(POPUP_KEY, String(Date.now())); } catch (e) {}
      }
      function popupOpen() {
        popup.hidden = false;
        var f = popup.querySelector('input[type="email"], [data-onpro-popup-close]');
        if (f) { try { f.focus(); } catch (e) {} }
      }
      function popupClose() { popup.hidden = true; popupRemember(); }

      $all('[data-onpro-popup-close]').forEach(function (b) {
        b.addEventListener('click', popupClose);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !popup.hidden) popupClose();
      });

      if (hasSuccess) {
        popupOpen();
        popupRemember();
      } else if (!popupDismissed()) {
        setTimeout(popupOpen, delayMs);
      }
    }

  });
})();
